import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import MascotPlaceholder from "@/components/MascotPlaceholder";
import { Button } from "@/components/ui/button";
import { shareCardImage } from "@/lib/shareCard";
import { trackEvent } from "@/lib/analytics";
import { ArrowRight, CaretUp, CaretDown, ShareNetwork, X } from "@phosphor-icons/react";

const MIN_SEEN_TO_UNLOCK = 5;
const MIN_TIME_TO_UNLOCK_MS = 35000; // 35s

// Warm-palette hues cycled per card index so consecutive stories feel
// distinct. All hues are dark enough to keep white text legible.
const CARD_HUES = [
  "#E07A5F", // terracotta (brand)
  "#C96849", // deep terracotta
  "#E39770", // peach terracotta
  "#B85F42", // brick red
  "#D68863", // rusty sand
  "#EA8A5E", // coral
];

// Swipe / tap thresholds
const SWIPE_UP_THRESHOLD = 60;    // px — dy to open deep dive
const SWIPE_DOWN_THRESHOLD = 60;  // px — dy to close deep dive
const TAP_MOVE_TOLERANCE = 10;    // px — beyond this, not a tap

export default function Explorativa() {
  const { t, lang, anonState, updateAnonState, anonReady } = useApp();
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [seenRaw, setSeenRaw] = useState(() => new Set(anonState.cards_seen || []));
  const [unlocked, setUnlocked] = useState(anonState.unlocked_reflect || false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showDeepDive, setShowDeepDive] = useState(false);
  const [direction, setDirection] = useState(0); // for enter animation
  const [sharing, setSharing] = useState(false);

  const pointerRef = useRef({ x: 0, y: 0, active: false });

  useEffect(() => {
    api.get(`/content/cards?lang=${lang}`).then(r => setCards(r.data));
  }, [lang]);

  const validIds = useMemo(() => new Set(cards.map(c => c.id)), [cards]);
  const seen = useMemo(
    () => new Set([...seenRaw].filter(id => validIds.has(id))),
    [seenRaw, validIds]
  );

  const markSeen = useCallback((id) => {
    setSeenRaw(prev => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      if (anonReady) updateAnonState({ cards_seen: [...next] });
      return next;
    });
  }, [anonReady, updateAnonState]);

  useEffect(() => {
    if (!cards.length) return;
    const c = cards[currentIndex];
    if (c) {
      markSeen(c.id);
      trackEvent("Card Opened", { id: c.id });
    }
  }, [currentIndex, cards, markSeen]);

  useEffect(() => {
    if (unlocked) return;
    const timer = setTimeout(() => setUnlocked(true), MIN_TIME_TO_UNLOCK_MS);
    return () => clearTimeout(timer);
  }, [unlocked]);

  useEffect(() => {
    if (seen.size >= MIN_SEEN_TO_UNLOCK && !unlocked) setUnlocked(true);
  }, [seen, unlocked]);

  const prev = useCallback(() => {
    if (showDeepDive) return;
    if (currentIndex <= 0) return;
    setDirection(-1);
    setCurrentIndex(i => i - 1);
  }, [currentIndex, showDeepDive]);

  const next = useCallback(() => {
    if (showDeepDive) return;
    if (currentIndex >= cards.length - 1) return;
    setDirection(1);
    setCurrentIndex(i => i + 1);
  }, [currentIndex, cards.length, showDeepDive]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowUp" && !showDeepDive && cards[currentIndex]?.deep_dive) {
        setShowDeepDive(true);
      } else if ((e.key === "ArrowDown" || e.key === "Escape") && showDeepDive) {
        setShowDeepDive(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, showDeepDive, cards, currentIndex]);

  const goQuiz = async (skipped) => {
    if (skipped) await updateAnonState({ unlocked_reflect: true });
    navigate("/quiz");
  };

  const shareCurrentCard = async () => {
    if (!card || sharing) return;
    setSharing(true);
    try {
      await shareCardImage({
        cardId: card.id,
        shortText: card.short,
        hue,
        appName: t.app.name,
        shareTitle: t.modes.explorativa.shareTitle,
        shareText: t.modes.explorativa.shareText,
      });
    } catch {
      // silently ignore — user likely dismissed the sheet
    } finally {
      setSharing(false);
    }
  };

  // ---- pointer gesture on the story area (tap zones + swipe-up) ----
  const isFromInteractive = (el) => !!el?.closest?.("[data-story-interactive]");

  const onPointerDown = (e) => {
    if (isFromInteractive(e.target)) return;
    pointerRef.current = { x: e.clientX, y: e.clientY, active: true };
  };
  const onPointerUp = (e) => {
    if (!pointerRef.current.active) return;
    pointerRef.current.active = false;
    if (isFromInteractive(e.target)) return;
    const dx = e.clientX - pointerRef.current.x;
    const dy = e.clientY - pointerRef.current.y;
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);

    // Swipe up → open deep dive (only if card has one and panel is closed)
    if (!showDeepDive && dy < -SWIPE_UP_THRESHOLD && absDy > absDx) {
      if (cards[currentIndex]?.deep_dive) setShowDeepDive(true);
      return;
    }
    // Tap → prev/next based on x half. Ignore if too much movement (drag/scroll).
    if (absDx < TAP_MOVE_TOLERANCE && absDy < TAP_MOVE_TOLERANCE) {
      const w = window.innerWidth;
      if (e.clientX < w / 2) prev(); else next();
    }
  };

  const card = cards[currentIndex];
  const hue = CARD_HUES[currentIndex % CARD_HUES.length];

  const storyVariants = {
    enter: (dir) => ({ opacity: 0, x: dir > 0 ? 30 : -30 }),
    center: { opacity: 1, x: 0 },
    exit: (dir) => ({ opacity: 0, x: dir > 0 ? -30 : 30 }),
  };

  return (
    <div
      data-testid="explorativa-stories"
      className="fixed inset-0 z-40 overflow-hidden select-none"
      style={{ backgroundColor: hue, transition: "background-color 360ms ease" }}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
    >
      {/* Segmented progress bar (Instagram Stories style) */}
      <div
        data-testid="story-progress"
        className="absolute top-3 left-3 right-3 flex gap-1 z-30 pointer-events-none"
      >
        {cards.map((c, idx) => (
          <div
            key={c.id}
            className="flex-1 h-[3px] rounded-full bg-white/25 overflow-hidden"
          >
            <div
              data-testid={`story-segment-${idx}`}
              className="h-full bg-white transition-all duration-500 ease-out"
              style={{ width: idx <= currentIndex ? "100%" : "0%" }}
            />
          </div>
        ))}
      </div>

      {/* Top-left mascot + top-right exit — pointer-events-auto only on the button */}
      <div className="absolute top-6 left-4 right-4 flex items-start justify-between z-20 pointer-events-none">
        <div data-story-interactive="mascot" className="pointer-events-auto">
          <MascotPlaceholder
            emotion="thinking"
            src="/assets/boardy-pensieroso.png"
            className="!w-[54px] sm:!w-[64px]"
          />
        </div>
        <div className="flex items-center gap-2 pointer-events-auto">
          <button
            data-testid="explorativa-share"
            data-story-interactive="share"
            onClick={shareCurrentCard}
            disabled={sharing}
            aria-label={t.modes.explorativa.share}
            className="w-10 h-10 rounded-full bg-white/15 backdrop-blur-sm text-white hover:bg-white/25 disabled:opacity-60 transition-colors flex items-center justify-center"
          >
            <ShareNetwork size={18} weight="bold" />
          </button>
          <button
            data-testid="explorativa-exit"
            data-story-interactive="exit"
            onClick={() => navigate("/")}
            aria-label={t.modes.explorativa.exit}
            className="w-10 h-10 rounded-full bg-white/15 backdrop-blur-sm text-white hover:bg-white/25 transition-colors flex items-center justify-center"
          >
            <X size={18} weight="bold" />
          </button>
        </div>
      </div>

      {/* Central card content — pointer-events-none so parent gesture handler wins */}
      <AnimatePresence mode="wait" custom={direction} initial={false}>
        {card && (
          <motion.div
            key={card.id}
            data-testid={`card-${card.id}`}
            data-card-id={card.id}
            custom={direction}
            variants={storyVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.28, ease: "easeOut" }}
            className="absolute inset-0 flex items-center justify-center px-6 sm:px-10 pointer-events-none"
          >
            <p
              className="font-display font-black text-white text-3xl sm:text-5xl leading-[1.15] text-center max-w-2xl tracking-tight drop-shadow-[0_2px_10px_rgba(0,0,0,0.15)]"
            >
              {card.short}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom stack: 'Scopri di più' + unlock CTA + skip link */}
      <div className="absolute bottom-6 left-0 right-0 flex flex-col items-center gap-3 z-20 px-6 pointer-events-none">
        {card?.deep_dive && !showDeepDive && (
          <button
            data-testid={`card-learn-more-${card.id}`}
            data-story-interactive="learn-more"
            onClick={() => setShowDeepDive(true)}
            className="pointer-events-auto inline-flex items-center gap-1.5 text-sm font-sans font-semibold text-white/95 hover:text-white transition-colors"
          >
            {t.modes.explorativa.learnMore}
            <CaretUp size={14} weight="bold" />
          </button>
        )}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 pointer-events-auto">
          <Button
            data-testid="reflect-cta"
            data-story-interactive="reflect"
            disabled={!unlocked}
            onClick={() => goQuiz(false)}
            className={`rounded-full h-12 px-6 font-sans font-bold transition-all ${
              unlocked
                ? "bg-white text-terracotta hover:bg-white/90 shadow-[0_10px_30px_rgba(0,0,0,0.15)]"
                : "bg-white/25 text-white/70 border-2 border-dashed border-white/40 cursor-not-allowed"
            }`}
          >
            {unlocked ? t.modes.explorativa.reflectCta : t.modes.explorativa.reflectDisabled}
            <ArrowRight size={18} weight="bold" className="ml-2" />
          </Button>
          <button
            data-testid="skip-to-quiz"
            data-story-interactive="skip"
            onClick={() => goQuiz(true)}
            className="text-sm text-white/85 underline underline-offset-4 hover:text-white transition-colors font-sans"
          >
            {t.modes.explorativa.skipNow}
          </button>
        </div>
      </div>

      {/* Deep-dive panel — slides up from the bottom, keeps the coloured hue
          visible above so the immersive vibe is preserved. */}
      <AnimatePresence>
        {showDeepDive && card?.deep_dive && (
          <>
            {/* Tap-to-close area above the panel */}
            <motion.div
              data-testid={`card-deep-dive-close-area-${card.id}`}
              data-story-interactive="deep-dive-close-area"
              className="absolute inset-x-0 top-0 z-40"
              style={{ height: "28vh" }}
              onClick={() => setShowDeepDive(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            />

            <motion.div
              data-testid={`card-deep-dive-${card.id}`}
              data-story-interactive="deep-dive-panel"
              drag="y"
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={{ top: 0, bottom: 0.4 }}
              onDragEnd={(e, info) => {
                if (info.offset.y > SWIPE_DOWN_THRESHOLD) setShowDeepDive(false);
              }}
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 32, stiffness: 260 }}
              className="absolute inset-x-0 bottom-0 top-[28vh] bg-surface rounded-t-[28px] z-50 shadow-[0_-20px_60px_rgba(0,0,0,0.25)] flex flex-col"
            >
              {/* Grabber handle */}
              <div className="pt-3 pb-2 flex justify-center shrink-0">
                <div className="w-10 h-1 rounded-full bg-ink-soft/30" />
              </div>

              <div className="flex-1 overflow-y-auto px-6 sm:px-10 pb-8">
                <p className="font-display font-normal text-xl sm:text-[22px] text-ink leading-snug tracking-tight max-w-2xl mx-auto">
                  {card.deep_dive}
                </p>
                <div className="mt-6 flex justify-center max-w-2xl mx-auto">
                  <button
                    data-testid={`card-close-deep-dive-${card.id}`}
                    data-story-interactive="deep-dive-close-btn"
                    onClick={() => setShowDeepDive(false)}
                    className="inline-flex items-center gap-1.5 text-sm font-sans font-semibold text-terracotta hover:text-terracotta-hover transition-colors"
                  >
                    {t.modes.explorativa.showLess}
                    <CaretDown size={14} weight="bold" />
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
